import React from 'react';
import { Plus, Trash2, X } from 'lucide-react';
import { DataGrid, GridCol } from '../ui/DataGrid';
import { TextCell, NumberCell, SaveState } from '../ui/EditableCell';
import { useRowAutosave } from '../ui/useRowAutosave';
import { Button } from '../ui/arc/button';
import { MetricCard } from '../ui/arc/metric-card';

// Costos fijos mensuales (pestaña Costos & BI → Costos Fijos) — rediseño 10/10/2026.
// Grilla con AUTOGUARDADO (useRowAutosave sobre `cost_fixed`, igual que Insumos): se edita
// en la celda, sin modo "editar" ni botón de guardar. Alta y borrado siguen usando los
// handlers de Costs.tsx (insert / delete con confirmación), sin cambios.

export interface FixedRow { id: number; name: string; amount_ars: number }

const CTL = 'h-9 min-w-0 rounded-[var(--radius-control)] border border-[var(--border-strong)] bg-card px-2.5 text-[length:var(--text-sm)] text-ink placeholder:text-faint focus:border-[var(--foreground)] focus:outline-none';

export function FijosPanel({
    rows, setRows, onDelete, showNew, setShowNew, newName, setNewName, newAmount, setNewAmount, onAdd,
}: {
    rows: FixedRow[];
    setRows: React.Dispatch<React.SetStateAction<FixedRow[]>>;
    onDelete: (id: number) => void;
    showNew: boolean;
    setShowNew: (v: boolean) => void;
    newName: string;
    setNewName: (v: string) => void;
    newAmount: string;
    setNewAmount: (v: string) => void;
    onAdd: () => void;
}) {
    const [err, setErr] = React.useState('');
    const { edit, state } = useRowAutosave<FixedRow>('cost_fixed', setRows, { onError: m => setErr(m) });

    const total = rows.reduce((a, r) => a + (Number(r.amount_ars) || 0), 0);
    const top = rows.reduce<FixedRow | null>((m, r) => (!m || r.amount_ars > m.amount_ars ? r : m), null);
    const fmt = (n: number) => '$' + Math.round(n).toLocaleString('es-AR');

    const cols: GridCol<FixedRow>[] = [
        {
            key: 'name', header: 'Concepto', sort: r => r.name,
            render: r => (
                <TextCell value={r.name} placeholder="Nombre del concepto"
                    onChange={v => v.trim() ? edit(r.id, { name: v }) : setRows(rs => rs.map(x => x.id === r.id ? { ...x, name: v } : x))} />
            ),
        },
        {
            key: 'amount', header: 'Monto mensual', align: 'right', width: 'w-48', sort: r => r.amount_ars,
            render: r => <NumberCell grouped prefix="$" value={r.amount_ars} onChange={v => edit(r.id, { amount_ars: Number(v) })} />,
        },
        {
            key: 'share', header: '% del total', align: 'right', width: 'w-40', hideOnMobile: true,
            sort: r => r.amount_ars,
            render: r => {
                const pct = total > 0 ? (r.amount_ars / total) * 100 : 0;
                return (
                    <span className="inline-flex w-full items-center justify-end gap-2">
                        <span className="h-1.5 w-16 overflow-hidden rounded-full bg-muted">
                            <span className="block h-full rounded-full bg-[var(--foreground)]" style={{ width: `${Math.min(100, pct)}%` }} />
                        </span>
                        <span className="w-12 text-right tabular-nums text-soft">{pct.toFixed(1)}%</span>
                    </span>
                );
            },
        },
        {
            key: 'actions', header: '', align: 'right', width: 'w-32',
            render: r => (
                <span className="inline-flex items-center justify-end gap-1">
                    <SaveState state={state[String(r.id)]} />
                    <Button variant="ghost" size="sm" aria-label={`Eliminar ${r.name}`} title="Eliminar"
                        onClick={() => onDelete(r.id)}><Trash2 size={14} /></Button>
                </span>
            ),
        },
    ];

    return (
        <div className="tw w-full min-w-0 pb-12">
            <header className="mb-5 flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h2 className="text-[length:var(--text-xl)] font-semibold leading-tight tracking-[var(--tracking-display)] text-ink">Costos fijos mensuales</h2>
                    <p className="mt-1 max-w-[72ch] text-[length:var(--text-sm)] text-soft">
                        Lo que se paga todos los meses se venda o no (alquiler, sueldos, servicios). Se usa en la Calculadora para el punto de equilibrio. Los cambios se guardan solos.
                    </p>
                </div>
                <Button onClick={() => setShowNew(!showNew)} variant={showNew ? 'secondary' : 'primary'}>
                    {showNew ? <><X size={16} /> Cancelar</> : <><Plus size={16} /> Nuevo costo</>}
                </Button>
            </header>

            <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
                <MetricCard label="Total mensual" prefix="$" value={Math.round(total)} context="suma de todos los conceptos (ARS)" locale="es-AR" />
                <MetricCard label="Por día" prefix="$" value={Math.round(total / 30)} context="total ÷ 30 días (ARS)" locale="es-AR" />
                <MetricCard label="Conceptos" value={rows.length} context="cargados" locale="es-AR" />
                <MetricCard label="Mayor costo" prefix="$" value={Math.round(top?.amount_ars || 0)} context={top ? top.name : '—'} locale="es-AR" />
            </div>

            {showNew && (
                <form className="mb-4 flex flex-wrap items-end gap-2 rounded-[var(--radius-surface)] border border-line bg-card p-4"
                    onSubmit={e => { e.preventDefault(); onAdd(); }}>
                    <label className="flex min-w-[14rem] flex-1 flex-col gap-1">
                        <span className="text-[length:var(--text-xs)] font-medium text-soft">Concepto</span>
                        <input autoFocus className={CTL} placeholder="Ej: Alquiler del taller" value={newName} onChange={e => setNewName(e.target.value)} />
                    </label>
                    <label className="flex w-48 flex-col gap-1">
                        <span className="text-[length:var(--text-xs)] font-medium text-soft">Monto mensual ($ ARS)</span>
                        <input type="number" inputMode="decimal" className={CTL + ' text-right tabular-nums'} placeholder="0" value={newAmount} onChange={e => setNewAmount(e.target.value)} />
                    </label>
                    <Button type="submit" disabled={!newName || !newAmount}>Agregar</Button>
                </form>
            )}

            {err && (
                <div className="mb-3 flex items-center justify-between rounded-[var(--radius-control)] border border-[color-mix(in_srgb,var(--danger)_40%,transparent)] bg-[color-mix(in_srgb,var(--danger)_8%,transparent)] px-3 py-2 text-[length:var(--text-sm)] text-[var(--danger)]">
                    <span>No se pudo guardar: {err}</span>
                    <button type="button" className="text-[var(--danger)]" onClick={() => setErr('')} aria-label="Cerrar"><X size={14} /></button>
                </div>
            )}

            <DataGrid<FixedRow>
                rows={rows} cols={cols} pageSize={50} dense
                searchPlaceholder="Buscar concepto…"
                emptyTitle="Sin costos fijos" emptyText="Cargá el primero para que la Calculadora pueda estimar el punto de equilibrio."
                emptyAction={<Button size="sm" onClick={() => setShowNew(true)}><Plus size={14} /> Nuevo costo</Button>}
                footer={rows.length > 0 ? (
                    <span className="text-[length:var(--text-sm)] text-soft">Total mensual: <b className="tabular-nums text-ink">{fmt(total)}</b></span>
                ) : undefined}
            />
        </div>
    );
}
